import React, { useEffect, useState, useCallback, useRef } from "react";
import {
  Form,
  Input,
  Select,
  Button,
  Card,
  InputNumber,
  Radio,
  Checkbox,
  Space,
  Divider,
} from "antd";
import { useAppDispatch, useAppSelector } from "Store/hooks";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeftOutlined } from "@ant-design/icons";
import {
  addPriceToProduct,
  updatePaddlePrice,
  getPaddleProduct,
  getPaddlePrice,
} from "../redux/thunks";
import { setLoadingState } from "Components/Basic/full_page_loader/redux/slice";
import { setErrorState } from "Components/Basic/ErrorBoundary/redux/slice";
import {
  ICreatePaddlePriceRequest,
  IUpdatePaddlePriceRequest,
} from "../redux/types";
import { selectFeatureGroupList } from "../../FeatureGroups/redux/selectors";
import { getAllFeatureGroups } from "../../FeatureGroups/redux/thunks";
import { selectSupportedFileFormats } from "Features/Tenants/LicenseManagement/redux/selectors";
import { getSupportedFileFormats } from "Features/Tenants/LicenseManagement/redux/thunks";
import { cloneDeep } from "lodash";
import FeatureSelectionModal from "../../FeatureGroups/FeatureSelectionModal/FeatureSelectionModal.index";
import PlanEditor from "Components/Custom/PlanEditor";
import { ensureUniqueKeys } from "Utilities/featureSummaryHelper";
import { ISubscriptionTier } from "../../SubscriptionTiers/redux/types";
import "./AddPriceToProductPage.index.css";

const { Option } = Select;
const { TextArea } = Input;

const AddPriceToProductPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { productId, priceId } = useParams<{
    productId: string;
    priceId: string;
  }>();
  const [form] = Form.useForm();
  const [priceType, setPriceType] = useState<"subscription" | "addon">(
    "subscription"
  );
  const [isEditMode, setIsEditMode] = useState(false);
  const [priceData, setPriceData] = useState<any>(null);
  const priceLoadedRef = useRef(false);
  const featuresPopulatedRef = useRef(false);

  const featureGroups = useAppSelector(selectFeatureGroupList);
  const supportedFileFormats = useAppSelector(selectSupportedFileFormats);
  const [duplicatedFeatureGroups, setDuplicatedFeatureGroups] = useState<any[]>(
    cloneDeep(featureGroups) || []
  );
  const [selectedLimits, setSelectedLimits] = useState<string[]>([]);

  const [selectedFeatureGroups, setSelectedFeatureGroups] = useState<string[]>(
    []
  );
  const [selectedRestrictions, setSelectedRestrictions] = useState<string[]>(
    []
  );
  const [isFeatureModalOpen, setIsFeatureModalOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [step0Values, setStep0Values] = useState<any>(null);

  const addOnType = Form.useWatch("addOnType", form);
  const hasQuantity = Form.useWatch("hasQuantity", form);
  const billingInterval = Form.useWatch("billingInterval", form);

  const [mockSubscriptionTier, setMockSubscriptionTier] =
    useState<ISubscriptionTier>({
      id: "-1",
      name: "",
      displayName: "",
      featureSummary: "",
      maxUsers: -1,
      maxAiTokens: -1,
      maxScheduledJobs: -1,
      maxRowsAllowed: -1,
      maxPagesAllowed: -1,
      maxValidationRulesAllowed: -1,
      maxApiConnectorsAllowed: -1,
      maxAutomationWorkflowsAllowed: -1,
      maxEmailIntegrationsAllowed: -1,
      maxNotificationRecipientsAllowed: -1,
      isActive: true,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      allFeatures: [],
      productEdition: "CenterpriseDataIntegrator" as any,
    });

  useEffect(() => {
    if (productId) {
      dispatch(getPaddleProduct(productId));
    }
    dispatch(getAllFeatureGroups());
    dispatch(getSupportedFileFormats());

    if (priceId && !priceLoadedRef.current) {
      setIsEditMode(true);
      priceLoadedRef.current = true;
      dispatch(getPaddlePrice({ productId: productId!, priceId })).then(
        (result) => {
          if (getPaddlePrice.fulfilled.match(result)) {
            setPriceData(result.payload);
          }
        }
      );
    }
  }, [dispatch, productId, priceId]);

  useEffect(() => {
    setDuplicatedFeatureGroups(cloneDeep(featureGroups) || []);
  }, [featureGroups]);

  useEffect(() => {
    if (!priceData || !isEditMode) return;

    const price = priceData;
    const priceName = price.customData?.price_name || "";
    const billingInterval =
      price.billingInterval === "annually" ? "yearly" : price.billingInterval;
    form.setFieldsValue({
      priceType: price.priceType,
      priceName: priceName,
      billingInterval: billingInterval,
      price: price.priceInCents / 100,
      currency: price.currency || "USD",
      description: price.description,
    });

    setPriceType(price.priceType as "subscription" | "addon");
  }, [priceData, isEditMode, form]);

  useEffect(() => {
    if (!priceData || !isEditMode || priceData.priceType !== "subscription")
      return;
    if (!featureGroups || featureGroups.length === 0) return;
    if (
      !priceData.customData ||
      Object.keys(priceData.customData).length === 0
    ) {
      return;
    }

    const customData = priceData.customData;

    if (customData.feature_groups) {
      let featureGroupNames: string[] = [];

      if (Array.isArray(customData.feature_groups)) {
        featureGroupNames = customData.feature_groups as string[];
      } else if (typeof customData.feature_groups === "string") {
        try {
          const parsed = JSON.parse(customData.feature_groups);
          featureGroupNames = Array.isArray(parsed) ? parsed : [];
        } catch {
          featureGroupNames = [customData.feature_groups];
        }
      }

      if (featureGroupNames.length > 0) {
        const featureGroupIds = featureGroupNames
          .map((name) => {
            const fg = featureGroups?.find((fg) => fg.name === name);
            return fg?.id;
          })
          .filter((id): id is string => id !== undefined);

        if (featureGroupIds.length > 0) {
          setSelectedFeatureGroups(featureGroupIds);
        } else {
          console.warn(
            "Could not find matching feature groups for names:",
            featureGroupNames
          );
        }
      }
    }
    if (customData.usage_limits) {
      let limits: any = customData.usage_limits;

      if (typeof limits === "string") {
        try {
          limits = JSON.parse(limits);
        } catch {
          console.error("Failed to parse usage_limits as JSON");
          limits = {};
        }
      }

      const restrictionFields: string[] = [];

      if (limits.max_users != null && limits.max_users > -1) {
        restrictionFields.push("maxUsers");
      }
      if (limits.max_ai_tokens != null && limits.max_ai_tokens > -1) {
        restrictionFields.push("maxAiTokens");
      }
      if (limits.max_scheduled_jobs != null && limits.max_scheduled_jobs > -1) {
        restrictionFields.push("maxScheduledJobs");
      }
      if (limits.max_rows_allowed != null && limits.max_rows_allowed > -1) {
        restrictionFields.push("maxRowsAllowed");
      }
      if (limits.max_pages_allowed != null && limits.max_pages_allowed > -1) {
        restrictionFields.push("maxPagesAllowed");
      }
      if (
        limits.max_validation_rules_allowed != null &&
        limits.max_validation_rules_allowed > -1
      ) {
        restrictionFields.push("maxValidationRulesAllowed");
      }
      if (
        (limits.max_api_connectors_allowed != null &&
        limits.max_api_connectors_allowed > -1) ||
        (limits.maxApiConnectorsAllowed != null &&
        limits.maxApiConnectorsAllowed > -1)
      ) {
        restrictionFields.push("maxApiConnectorsAllowed");
      }
      if (
        limits.max_automation_workflows_allowed != null &&
        limits.max_automation_workflows_allowed > -1
      ) {
        restrictionFields.push("maxAutomationWorkflowsAllowed");
      }
      if (
        limits.max_email_integrations_allowed != null &&
        limits.max_email_integrations_allowed > -1
      ) {
        restrictionFields.push("maxEmailIntegrationsAllowed");
      }
      if (
        limits.max_notification_recipients_allowed != null &&
        limits.max_notification_recipients_allowed > -1
      ) {
        restrictionFields.push("maxNotificationRecipientsAllowed");
      }
      if (
        Array.isArray(limits.supported_input_formats) &&
        limits.supported_input_formats.length > 0
      ) {
        restrictionFields.push("supportedInputFormats");
      }
      if (
        Array.isArray(limits.supported_output_formats) &&
        limits.supported_output_formats.length > 0
      ) {
        restrictionFields.push("supportedOutputFormats");
      }

      if (restrictionFields.length > 0) {
        setSelectedRestrictions(restrictionFields);
        form.setFieldsValue({
          selectedRestrictions: restrictionFields,
          maxUsers: limits.max_users != null ? limits.max_users : undefined,
          maxAiTokens:
            limits.max_ai_tokens != null ? limits.max_ai_tokens : undefined,
          maxScheduledJobs:
            limits.max_scheduled_jobs != null
              ? limits.max_scheduled_jobs
              : undefined,
          maxRowsAllowed:
            limits.max_rows_allowed != null
              ? limits.max_rows_allowed
              : undefined,
          maxPagesAllowed:
            limits.max_pages_allowed != null
              ? limits.max_pages_allowed
              : undefined,
          maxValidationRulesAllowed:
            limits.max_validation_rules_allowed != null
              ? limits.max_validation_rules_allowed
              : undefined,
          maxApiConnectorsAllowed:
            limits.max_api_connectors_allowed != null
              ? limits.max_api_connectors_allowed
              : limits.maxApiConnectorsAllowed != null
              ? limits.maxApiConnectorsAllowed
              : undefined,
          maxAutomationWorkflowsAllowed:
            limits.max_automation_workflows_allowed != null
              ? limits.max_automation_workflows_allowed
              : undefined,
          maxEmailIntegrationsAllowed:
            limits.max_email_integrations_allowed != null
              ? limits.max_email_integrations_allowed
              : undefined,
          maxNotificationRecipientsAllowed:
            limits.max_notification_recipients_allowed != null
              ? limits.max_notification_recipients_allowed
              : undefined,
        });
      }

      const inputFormats = limits.supported_input_formats;
      const outputFormats = limits.supported_output_formats;
      if (Array.isArray(inputFormats) || Array.isArray(outputFormats)) {
        form.setFieldsValue({
          supportedInputFormats: Array.isArray(inputFormats) ? inputFormats : [],
          supportedOutputFormats: Array.isArray(outputFormats) ? outputFormats : [],
        });
      }
    }

    let featureSummary: string = "";
    if (customData.feature_summary) {
      if (typeof customData.feature_summary === "string") {
        featureSummary = customData.feature_summary;
      } else {
        featureSummary = JSON.stringify(customData.feature_summary);
      }
    }

    setMockSubscriptionTier((prev) => ({
      ...prev,
      featureSummary: featureSummary,
    }));
  }, [priceData, isEditMode, form, featureGroups]);

  useEffect(() => {
    if (!priceData || !isEditMode || priceData.priceType !== "addon") return;
    if (!priceData.customData) return;

    const customData = priceData.customData;
    const billingInterval =
      priceData.billingInterval === "annually"
        ? "yearly"
        : priceData.billingInterval;
    if (customData.addon_details) {
      const addonDetails = customData.addon_details as any;
      form.setFieldsValue({
        addOnType: addonDetails.addon_type,
        addOnName: addonDetails.addon_name,
        quantity: addonDetails.quantity,
        billingInterval: billingInterval,
        hasQuantity: addonDetails.has_quantity,
        extraUsers: addonDetails.extra_users,
        extraAiTokens: addonDetails.extra_ai_tokens,
        extraScheduledJobs: addonDetails.extra_scheduled_jobs,
        extraRowsProcessed: addonDetails.extra_rows_processed,
        extraPagesAllowed: addonDetails.extra_pages_allowed,
        extraValidationRulesAllowed:
          addonDetails.extra_validation_rules_allowed,
        extraApiConnectorsAllowed: addonDetails.extra_api_connectors_allowed,
        extraAutomationWorkflowsAllowed:
          addonDetails.extra_automation_workflows_allowed,
        extraEmailIntegrationsAllowed:
          addonDetails.extra_email_integrations_allowed,
      });

      const limitFields: string[] = [];

      if (addonDetails.extra_users != null && addonDetails.extra_users > -1) {
        limitFields.push("extraUsers");
      }
      if (
        addonDetails.extra_ai_tokens != null &&
        addonDetails.extra_ai_tokens > -1
      ) {
        limitFields.push("extraAiTokens");
      }
      if (
        addonDetails.extra_scheduled_jobs != null &&
        addonDetails.extra_scheduled_jobs > -1
      ) {
        limitFields.push("extraScheduledJobs");
      }
      if (
        addonDetails.extra_rows_processed != null &&
        addonDetails.extra_rows_processed > -1
      ) {
        limitFields.push("extraRowsProcessed");
      }
      if (
        addonDetails.extra_pages_allowed != null &&
        addonDetails.extra_pages_allowed > -1
      ) {
        limitFields.push("extraPagesAllowed");
      }
      if (
        addonDetails.extra_validation_rules_allowed != null &&
        addonDetails.extra_validation_rules_allowed > -1
      ) {
        limitFields.push("extraValidationRulesAllowed");
      }
      if (
        addonDetails.extra_api_connectors_allowed != null &&
        addonDetails.extra_api_connectors_allowed > -1
      ) {
        limitFields.push("extraApiConnectorsAllowed");
      }
      if (
        addonDetails.extra_automation_workflows_allowed != null &&
        addonDetails.extra_automation_workflows_allowed > -1
      ) {
        limitFields.push("extraAutomationWorkflowsAllowed");
      }
      if (
        addonDetails.extra_email_integrations_allowed != null &&
        addonDetails.extra_email_integrations_allowed > -1
      ) {
        limitFields.push("extraEmailIntegrationsAllowed");
      }

      if (limitFields.length > 0) {
        setSelectedLimits(limitFields);
        form.setFieldsValue({
          selectedLimits: limitFields,
        });
      } else {
        setSelectedLimits([]);
        form.setFieldsValue({
          selectedLimits: [],
        });
      }
    }
  }, [priceData, isEditMode, form]);

  useEffect(() => {
    if (
      !priceData ||
      !isEditMode ||
      priceData.priceType !== "addon" ||
      !priceData.customData
    )
      return;
    if (!featureGroups || featureGroups.length === 0) return;
    if (featuresPopulatedRef.current) return;

    const customData = priceData.customData;

    if (customData.addon_details) {
      const addonDetails = customData.addon_details as any;
      if (addonDetails.features && Array.isArray(addonDetails.features)) {
        const featureGroupNames = addonDetails.features as string[];
        const selectedFeatureIds = featureGroups
          .filter((fg: any) => featureGroupNames.includes(fg.name))
          .map((fg: any) => fg.id);
        if (selectedFeatureIds.length > 0) {
          form.setFieldsValue({
            selectedFeatures: selectedFeatureIds,
          });
          featuresPopulatedRef.current = true;
        }
      }
    }
  }, [priceData, featureGroups, isEditMode, form]);

  const handlePriceTypeChange = (e: any) => {
    setPriceType(e.target.value);
    form.setFieldsValue({
      addOnType: undefined,
      selectedFeatures: [],
      selectedLimits: [],
      selectedRestrictions: [],
      supportedInputFormats: [],
      supportedOutputFormats: [],
    });
    setSelectedLimits([]);
    setSelectedFeatureGroups([]);
    setSelectedRestrictions([]);
    setCurrentStep(0);
    setStep0Values(null);
  };

  const handleNext = () => {
    form
      .validateFields([
        "priceType",
        "priceName",
        "billingInterval",
        "price",
        "currency",
        "description",
      ])
      .then(() => {
        const step0FormValues = form.getFieldsValue();
        setStep0Values(step0FormValues);
        setCurrentStep(1);
      })
      .catch(() => {});
  };

  const handlePrevious = () => {
    setCurrentStep(0);
    if (step0Values) {
      form.setFieldsValue(step0Values);
    }
  };

  const handleFeatureConfirm = (selectedIds: string[]) => {
    setSelectedFeatureGroups(selectedIds);
    setIsFeatureModalOpen(false);
  };

  const selectedFeatureNames = selectedFeatureGroups
    .map((id) => featureGroups?.find((fg) => fg.id === id)?.displayName)
    .filter((name): name is string => name !== undefined);

  const handleAddOnTypeChange = (e: any) => {
    const checkedValues = e.target.value;

    form.setFieldsValue({
      addOnType: checkedValues,
    });

    if (!checkedValues.includes("FeatureAddOn")) {
      form.setFieldsValue({ selectedFeatures: [] });
    }
    if (!checkedValues.includes("EnhancedLimitsAddOn")) {
      setSelectedLimits([]);
      form.setFieldsValue({ selectedLimits: [] });
    }
  };

  const handleLimitsChange = (values: string[]) => {
    setSelectedLimits(values);
    form.setFieldsValue({ selectedLimits: values });
  };

  const handleSearchFeatureGroups = useCallback(
    (value: string) => {
      if (!value || value.trim() === "") {
        setDuplicatedFeatureGroups(cloneDeep(featureGroups) || []);
      } else {
        const filteredFeatures = (featureGroups || []).filter((feature: any) =>
          feature.displayName.toLowerCase().includes(value.toLowerCase())
        );
        setDuplicatedFeatureGroups(filteredFeatures);
      }
    },
    [featureGroups]
  );

  const onFinish = async (values: any) => {
    try {
      if (!productId) return;

      if (priceType === "subscription" && currentStep === 0) {
        handleNext();
        return;
      }

      dispatch(setLoadingState(true));

      const finalValues =
        priceType === "subscription" && currentStep === 1 && step0Values
          ? { ...step0Values, ...values }
          : values;

      const priceInCents = Math.round((finalValues.price || 0) * 100);

      const billingInterval =
        finalValues.billingInterval === "yearly"
          ? "annually"
          : finalValues.billingInterval;

      const request: ICreatePaddlePriceRequest = {
        productId,
        pricing: {
          billingInterval: billingInterval,
          priceInCents: priceInCents,
          currency: finalValues.currency || "USD",
          description: finalValues.description,
        },
        priceType: priceType,
        priceName:
          priceType === "subscription"
            ? finalValues.priceName
            : finalValues.addOnName,
      };

      if (priceType === "subscription") {
        const featureGroupNames = selectedFeatureGroups
          .map((id) => featureGroups?.find((fg) => fg.id === id)?.name)
          .filter((name): name is string => name !== undefined);

        request.featureGroups = featureGroupNames;

        const restrictionsObj: any = {
          maxUsers: selectedRestrictions.includes("maxUsers") ? (finalValues.maxUsers ?? -1) : -1,
          maxAiTokens: selectedRestrictions.includes("maxAiTokens") ? (finalValues.maxAiTokens ?? -1) : -1,
          maxScheduledJobs: selectedRestrictions.includes("maxScheduledJobs") ? (finalValues.maxScheduledJobs ?? -1) : -1,
          maxRowsAllowed: selectedRestrictions.includes("maxRowsAllowed") ? (finalValues.maxRowsAllowed ?? -1) : -1,
          maxPagesAllowed: selectedRestrictions.includes("maxPagesAllowed") ? (finalValues.maxPagesAllowed ?? -1) : -1,
          maxValidationRulesAllowed: selectedRestrictions.includes("maxValidationRulesAllowed") ? (finalValues.maxValidationRulesAllowed ?? -1) : -1,
          maxApiConnectorsAllowed: selectedRestrictions.includes("maxApiConnectorsAllowed") ? (finalValues.maxApiConnectorsAllowed ?? -1) : -1,
          maxAutomationWorkflowsAllowed: selectedRestrictions.includes("maxAutomationWorkflowsAllowed") ? (finalValues.maxAutomationWorkflowsAllowed ?? -1) : -1,
          maxEmailIntegrationsAllowed: selectedRestrictions.includes("maxEmailIntegrationsAllowed") ? (finalValues.maxEmailIntegrationsAllowed ?? -1) : -1,
          maxNotificationRecipientsAllowed: selectedRestrictions.includes("maxNotificationRecipientsAllowed") ? (finalValues.maxNotificationRecipientsAllowed ?? -1) : -1,
          supportedInputFormats: selectedRestrictions.includes("supportedInputFormats") && Array.isArray(finalValues.supportedInputFormats) ? finalValues.supportedInputFormats : [],
          supportedOutputFormats: selectedRestrictions.includes("supportedOutputFormats") && Array.isArray(finalValues.supportedOutputFormats) ? finalValues.supportedOutputFormats : [],
        };

        request.restrictions = restrictionsObj;

        if (
          finalValues.featureSummaryList &&
          Array.isArray(finalValues.featureSummaryList) &&
          finalValues.featureSummaryList.length > 0
        ) {
          request.featureSummary = JSON.stringify(
            ensureUniqueKeys(finalValues.featureSummaryList).sort(
              (a: any, b: any) => (a.order ?? 0) - (b.order ?? 0)
            )
          );
        }
      }

      if (priceType === "addon") {
        const selectedFeatureIds = finalValues.selectedFeatures || [];
        const featureGroupNames = selectedFeatureIds
          .map((id: string) => {
            const feature = featureGroups?.find((fg) => fg.id === id);
            return feature?.name;
          })
          .filter(
            (name: string | undefined): name is string => name !== undefined
          );

        request.addOnDetails = {
          addOnType: finalValues.addOnType,
          addOnName: finalValues.addOnName,
          quantity: finalValues.quantity || -1,
          hasQuantity: finalValues.hasQuantity || false,
          extraUsers: finalValues.extraUsers || -1,
          extraAiTokens: finalValues.extraAiTokens || -1,
          extraScheduledJobs: finalValues.extraScheduledJobs || -1,
          extraRowsProcessed: finalValues.extraRowsProcessed || -1,
          extraPagesAllowed: finalValues.extraPagesAllowed || -1,
          extraValidationRulesAllowed:
            finalValues.extraValidationRulesAllowed || -1,
          extraApiConnectorsAllowed:
            finalValues.extraApiConnectorsAllowed || -1,
          extraAutomationWorkflowsAllowed:
            finalValues.extraAutomationWorkflowsAllowed || -1,
          extraEmailIntegrationsAllowed:
            finalValues.extraEmailIntegrationsAllowed || -1,
          features: featureGroupNames,
        };

        request.featureGroups = featureGroupNames;
      }

      if (isEditMode && priceId) {
        const updateRequest: IUpdatePaddlePriceRequest = {
          description: finalValues.description,
          priceInCents: priceInCents,
          priceName:
            priceType === "subscription"
              ? finalValues.priceName
              : finalValues.addOnName,
        };

        if (priceType === "subscription") {
          const featureGroupNames = selectedFeatureGroups
            .map((id) => featureGroups?.find((fg) => fg.id === id)?.name)
            .filter((name): name is string => name !== undefined);

          updateRequest.featureGroups = featureGroupNames;

          const restrictionsObj: any = {
            maxUsers: selectedRestrictions.includes("maxUsers") ? (finalValues.maxUsers ?? -1) : -1,
            maxAiTokens: selectedRestrictions.includes("maxAiTokens") ? (finalValues.maxAiTokens ?? -1) : -1,
            maxScheduledJobs: selectedRestrictions.includes("maxScheduledJobs") ? (finalValues.maxScheduledJobs ?? -1) : -1,
            maxRowsAllowed: selectedRestrictions.includes("maxRowsAllowed") ? (finalValues.maxRowsAllowed ?? -1) : -1,
            maxPagesAllowed: selectedRestrictions.includes("maxPagesAllowed") ? (finalValues.maxPagesAllowed ?? -1) : -1,
            maxValidationRulesAllowed: selectedRestrictions.includes("maxValidationRulesAllowed") ? (finalValues.maxValidationRulesAllowed ?? -1) : -1,
            maxApiConnectorsAllowed: selectedRestrictions.includes("maxApiConnectorsAllowed") ? (finalValues.maxApiConnectorsAllowed ?? -1) : -1,
            maxAutomationWorkflowsAllowed: selectedRestrictions.includes("maxAutomationWorkflowsAllowed") ? (finalValues.maxAutomationWorkflowsAllowed ?? -1) : -1,
            maxEmailIntegrationsAllowed: selectedRestrictions.includes("maxEmailIntegrationsAllowed") ? (finalValues.maxEmailIntegrationsAllowed ?? -1) : -1,
            supportedInputFormats: selectedRestrictions.includes("supportedInputFormats") && Array.isArray(finalValues.supportedInputFormats) ? finalValues.supportedInputFormats : [],
            supportedOutputFormats: selectedRestrictions.includes("supportedOutputFormats") && Array.isArray(finalValues.supportedOutputFormats) ? finalValues.supportedOutputFormats : [],
          };

          updateRequest.restrictions = restrictionsObj;

          if (
            finalValues.featureSummaryList &&
            Array.isArray(finalValues.featureSummaryList) &&
            finalValues.featureSummaryList.length > 0
          ) {
            updateRequest.featureSummary = JSON.stringify(
              ensureUniqueKeys(finalValues.featureSummaryList).sort(
                (a: any, b: any) => (a.order ?? 0) - (b.order ?? 0)
              )
            );
          }
        }

        if (priceType === "addon") {
          const selectedFeatureIds = finalValues.selectedFeatures || [];
          const featureGroupNames = selectedFeatureIds
            .map((id: string) => {
              const feature = featureGroups?.find((fg) => fg.id === id);
              return feature?.name;
            })
            .filter(
              (name: string | undefined): name is string => name !== undefined
            );

          updateRequest.featureGroups = featureGroupNames;
          updateRequest.addOnDetails = {
            addOnType: finalValues.addOnType,
            addOnName: finalValues.addOnName,
            quantity: finalValues.quantity || -1,
            hasQuantity: finalValues.hasQuantity || false,
            extraUsers: finalValues.extraUsers || -1,
            extraAiTokens: finalValues.extraAiTokens || -1,
            extraScheduledJobs: finalValues.extraScheduledJobs || -1,
            extraRowsProcessed: finalValues.extraRowsProcessed || -1,
            extraPagesAllowed: finalValues.extraPagesAllowed || -1,
            features: featureGroupNames,
            extraValidationRulesAllowed:
              finalValues.extraValidationRulesAllowed || -1,
            extraApiConnectorsAllowed:
              finalValues.extraApiConnectorsAllowed || -1,
            extraAutomationWorkflowsAllowed:
              finalValues.extraAutomationWorkflowsAllowed || -1,
            extraEmailIntegrationsAllowed:
              finalValues.extraEmailIntegrationsAllowed || -1,
          };

          if (selectedLimits.length > 0) {
            updateRequest.restrictions = {
              maxUsers: selectedLimits.includes("maxUsers")
                ? finalValues.maxUsers || -1
                : -1,
              maxAiTokens: selectedLimits.includes("maxAiTokens")
                ? finalValues.maxAiTokens || -1
                : -1,
              maxScheduledJobs: selectedLimits.includes("maxScheduledJobs")
                ? finalValues.maxScheduledJobs || -1
                : -1,
              maxRowsAllowed: selectedLimits.includes("maxRowsAllowed")
                ? finalValues.maxRowsAllowed || -1
                : -1,
              maxPagesAllowed: selectedLimits.includes("maxPagesAllowed")
                ? finalValues.maxPagesAllowed || -1
                : -1,
              maxValidationRulesAllowed: selectedLimits.includes(
                "maxValidationRulesAllowed"
              )
                ? finalValues.maxValidationRulesAllowed || -1
                : -1,
              maxApiConnectorsAllowed: selectedLimits.includes(
                "maxApiConnectorsAllowed"
              )
                ? finalValues.maxApiConnectorsAllowed || -1
                : -1,
              maxAutomationWorkflowsAllowed: selectedLimits.includes(
                "maxAutomationWorkflowsAllowed"
              )
                ? finalValues.maxAutomationWorkflowsAllowed || -1
                : -1,
              maxEmailIntegrationsAllowed: selectedLimits.includes(
                "maxEmailIntegrationsAllowed"
              )
                ? finalValues.maxEmailIntegrationsAllowed || -1
                : -1,
              maxNotificationRecipientsAllowed: selectedLimits.includes(
                "maxNotificationRecipientsAllowed"
              )
                ? finalValues.maxNotificationRecipientsAllowed || -1
                : -1,
            };
          }
        }
        await dispatch(
          updatePaddlePrice({ productId, priceId, request: updateRequest })
        ).unwrap();

        dispatch(
          setErrorState({
            message: "Success",
            description: "Price updated successfully",
            placement: "topRight",
            type: "success",
          })
        );
      } else {
        await dispatch(addPriceToProduct({ productId, request })).unwrap();

        dispatch(
          setErrorState({
            message: "Success",
            description: "Price added successfully to product",
            placement: "topRight",
            type: "success",
          })
        );
      }

      navigate(`/payment-products`);
    } catch (err: any) {
      dispatch(
        setErrorState({
          message: "Error",
          description: err.message || "Failed to add price",
          placement: "topRight",
          type: "error",
        })
      );
    } finally {
      dispatch(setLoadingState(false));
    }
  };

  const handleCancel = () => {
    setStep0Values(null);
    setCurrentStep(0);
    form.resetFields();
    navigate("/payment-products");
  };

  return (
    <div className="add-price-to-product-page-container">
      <div className="add-price-to-product-page-content">
        <Card className="add-price-to-product-page-card">
          <div className="page-header">
            <Button icon={<ArrowLeftOutlined />} onClick={handleCancel}>
              Back
            </Button>
            <h2>
              {isEditMode
                ? currentStep === 0
                  ? "Edit Price"
                  : "Configure Plan Details"
                : currentStep === 0
                ? "Add Price to Product"
                : "Configure Plan Details"}
            </h2>
          </div>

          <Divider />

          <Form form={form} layout="vertical" onFinish={onFinish}>
            {(priceType !== "subscription" || currentStep === 0) && (
              <>
                <Form.Item
                  label="Price Type"
                  name="priceType"
                  rules={[
                    { required: true, message: "Please select price type" },
                  ]}
                >
                  <Radio.Group
                    onChange={handlePriceTypeChange}
                    disabled={isEditMode}
                  >
                    <Radio value="subscription">Subscription</Radio>
                    <Radio value="addon">Add-On</Radio>
                  </Radio.Group>
                  {isEditMode && (
                    <div
                      style={{
                        fontSize: "12px",
                        color: "#999",
                        marginTop: "4px",
                      }}
                    >
                      Price type cannot be changed after creation
                    </div>
                  )}
                </Form.Item>

                {priceType === "subscription" ? (
                  <Form.Item
                    label="Price Name"
                    name="priceName"
                    rules={[
                      { required: true, message: "Please enter price name" },
                    ]}
                  >
                    <Input placeholder="Enter price name (e.g., Monthly Plan, Annual Plan)" />
                  </Form.Item>
                ) : (
                  <Form.Item
                    label="Add-On Name"
                    name="addOnName"
                    rules={[
                      { required: true, message: "Please enter add-on name" },
                    ]}
                  >
                    <Input placeholder="Enter add-on name (e.g., ExtraUsers, SnowflakeConnector)" />
                  </Form.Item>
                )}

                {priceType === "subscription" && (
                  <>
                    <Form.Item label="Feature Groups">
                      <Space direction="vertical" style={{ width: "100%" }}>
                        <Button onClick={() => setIsFeatureModalOpen(true)}>
                          {selectedFeatureGroups.length > 0
                            ? `Edit Features (${selectedFeatureGroups.length} selected)`
                            : "Select Feature Groups"}
                        </Button>
                        {selectedFeatureNames.length > 0 && (
                          <div style={{ marginTop: 8 }}>
                            <strong>Selected:</strong>{" "}
                            {selectedFeatureNames.join(", ")}
                          </div>
                        )}
                      </Space>
                    </Form.Item>

                    <Form.Item>
                      <Space direction="vertical" style={{ width: "100%" }}>
                        <Form.Item
                          label="Select Restrictions"
                          name="selectedRestrictions"
                          style={{ marginBottom: 0 }}
                        >
                          <Select
                            mode="multiple"
                            allowClear
                            placeholder="Please select the restrictions"
                            value={selectedRestrictions}
                            onChange={(values: string[]) => {
                              setSelectedRestrictions(values);
                              form.setFieldsValue({
                                selectedRestrictions: values,
                              });
                              form.setFieldsValue({
                                maxUsers: values.includes("maxUsers")
                                  ? form.getFieldValue("maxUsers")
                                  : undefined,
                                maxAiTokens: values.includes("maxAiTokens")
                                  ? form.getFieldValue("maxAiTokens")
                                  : undefined,
                                maxScheduledJobs: values.includes(
                                  "maxScheduledJobs"
                                )
                                  ? form.getFieldValue("maxScheduledJobs")
                                  : undefined,
                                maxRowsAllowed: values.includes(
                                  "maxRowsAllowed"
                                )
                                  ? form.getFieldValue("maxRowsAllowed")
                                  : undefined,
                                maxPagesAllowed: values.includes(
                                  "maxPagesAllowed"
                                )
                                  ? form.getFieldValue("maxPagesAllowed")
                                  : undefined,
                                maxValidationRulesAllowed: values.includes(
                                  "maxValidationRulesAllowed"
                                )
                                  ? form.getFieldValue("maxValidationRulesAllowed")
                                  : undefined,
                                maxApiConnectorsAllowed: values.includes(
                                  "maxApiConnectorsAllowed"
                                )
                                  ? form.getFieldValue("maxApiConnectorsAllowed")
                                  : undefined,
                                maxAutomationWorkflowsAllowed: values.includes(
                                  "maxAutomationWorkflowsAllowed"
                                )
                                  ? form.getFieldValue("maxAutomationWorkflowsAllowed")
                                  : undefined,
                                maxEmailIntegrationsAllowed: values.includes(
                                  "maxEmailIntegrationsAllowed"
                                )
                                  ? form.getFieldValue("maxEmailIntegrationsAllowed")
                                  : undefined,
                                maxNotificationRecipientsAllowed: values.includes(
                                  "maxNotificationRecipientsAllowed"
                                )
                                  ? form.getFieldValue("maxNotificationRecipientsAllowed")
                                  : undefined,
                                supportedInputFormats: values.includes(
                                  "supportedInputFormats"
                                )
                                  ? form.getFieldValue("supportedInputFormats")
                                  : [],
                                supportedOutputFormats: values.includes(
                                  "supportedOutputFormats"
                                )
                                  ? form.getFieldValue("supportedOutputFormats")
                                  : [],
                              });
                            }}
                          >
                            <Option value="maxUsers">Max Users</Option>
                            <Option value="maxAiTokens">Max AI Tokens</Option>
                            <Option value="maxScheduledJobs">
                              Max Scheduled Jobs
                            </Option>
                            <Option value="maxRowsAllowed">
                              Max Rows Allowed
                            </Option>
                            <Option value="maxPagesAllowed">
                              Max Pages Allowed
                            </Option>
                            <Option value="maxValidationRulesAllowed">
                              Max Validation Rules Allowed
                            </Option>
                            <Option value="maxApiConnectorsAllowed">
                              Max Api Connectors Allowed
                            </Option>
                            <Option value="maxAutomationWorkflowsAllowed">
                              Max Automation Workflows Allowed
                            </Option>
                            <Option value="maxEmailIntegrationsAllowed">
                              Max Email Integrations Allowed
                            </Option>
                            <Option value="maxNotificationRecipientsAllowed">
                              Max Notification Recipients Allowed
                            </Option>
                            <Option value="supportedInputFormats">
                              Supported File Input Formats (Upload)
                            </Option>
                            <Option value="supportedOutputFormats">
                              Supported File Output Formats (Export/Download)
                            </Option>
                          </Select>
                        </Form.Item>

                        {selectedRestrictions.length > 0 && (
                          <Form.Item
                            label="Please Define the Restrictions"
                            style={{ marginBottom: 1, marginTop: 4 }}
                          >
                            {selectedRestrictions.includes("maxUsers") && (
                              <Form.Item
                                label="Max Users"
                                name="maxUsers"
                                rules={[
                                  {
                                    required: true,
                                    message: "Please enter max users",
                                  },
                                ]}
                                style={{ marginBottom: 8 }}
                              >
                                <InputNumber
                                  placeholder="Enter max users"
                                  style={{ width: "100%" }}
                                  min={0}
                                />
                              </Form.Item>
                            )}
                            {selectedRestrictions.includes("maxAiTokens") && (
                              <Form.Item
                                label="Max AI Tokens"
                                name="maxAiTokens"
                                rules={[
                                  {
                                    required: true,
                                    message: "Please enter max AI tokens",
                                  },
                                ]}
                                style={{ marginBottom: 8 }}
                              >
                                <InputNumber
                                  placeholder="Enter max AI tokens"
                                  style={{ width: "100%" }}
                                  min={0}
                                />
                              </Form.Item>
                            )}
                            {selectedRestrictions.includes(
                              "maxScheduledJobs"
                            ) && (
                              <Form.Item
                                label="Max Scheduled Jobs"
                                name="maxScheduledJobs"
                                rules={[
                                  {
                                    required: true,
                                    message: "Please enter max scheduled jobs",
                                  },
                                ]}
                                style={{ marginBottom: 8 }}
                              >
                                <InputNumber
                                  placeholder="Enter max scheduled jobs"
                                  style={{ width: "100%" }}
                                  min={0}
                                />
                              </Form.Item>
                            )}
                            {selectedRestrictions.includes(
                              "maxRowsAllowed"
                            ) && (
                              <Form.Item
                                label="Max Rows Allowed"
                                name="maxRowsAllowed"
                                rules={[
                                  {
                                    required: true,
                                    message: "Please enter max rows allowed",
                                  },
                                ]}
                                style={{ marginBottom: 8 }}
                              >
                                <InputNumber
                                  placeholder="Enter max rows allowed"
                                  style={{ width: "100%" }}
                                  min={0}
                                />
                              </Form.Item>
                            )}
                            {selectedRestrictions.includes(
                              "maxPagesAllowed"
                            ) && (
                              <Form.Item
                                label="Max Pages Allowed"
                                name="maxPagesAllowed"
                                rules={[
                                  {
                                    required: true,
                                    message: "Please enter max pages allowed",
                                  },
                                ]}
                                style={{ marginBottom: 8 }}
                              >
                                <InputNumber
                                  placeholder="Enter max pages allowed"
                                  style={{ width: "100%" }}
                                  min={0}
                                />
                              </Form.Item>
                            )}
                            {selectedRestrictions.includes(
                              "maxValidationRulesAllowed"
                            ) && (
                              <Form.Item
                                label="Max Validation Rules Allowed"
                                name="maxValidationRulesAllowed"
                              >
                                <InputNumber
                                  placeholder="Enter max validation rules allowed"
                                  style={{ width: "100%" }}
                                  min={0}
                                />
                              </Form.Item>
                            )}
                            {selectedRestrictions.includes(
                              "maxApiConnectorsAllowed"
                            ) && (
                              <Form.Item
                                label="Max Api Connectors Allowed"
                                name="maxApiConnectorsAllowed"
                              >
                                <InputNumber
                                  placeholder="Enter max api connectors allowed"
                                  style={{ width: "100%" }}
                                  min={0}
                                />
                              </Form.Item>
                            )}
                            {selectedRestrictions.includes(
                              "maxAutomationWorkflowsAllowed"
                            ) && (
                              <Form.Item
                                label="Max Automation Workflows Allowed"
                                name="maxAutomationWorkflowsAllowed"
                              >
                                <InputNumber
                                  placeholder="Enter max automation workflows allowed"
                                  style={{ width: "100%" }}
                                  min={0}
                                />
                              </Form.Item>
                            )}
                            {selectedRestrictions.includes(
                              "maxEmailIntegrationsAllowed"
                            ) && (
                              <Form.Item
                                label="Max Email Integrations Allowed"
                                name="maxEmailIntegrationsAllowed"
                              >
                                <InputNumber
                                  placeholder="Enter max email integrations allowed"
                                  style={{ width: "100%" }}
                                  min={0}
                                />
                              </Form.Item>
                            )}
                            {selectedRestrictions.includes(
                              "maxNotificationRecipientsAllowed"
                            ) && (
                              <Form.Item
                                label="Max Notification Recipients Allowed"
                                name="maxNotificationRecipientsAllowed"
                              >
                                <InputNumber
                                  placeholder="Enter max notification recipients allowed"
                                  style={{ width: "100%" }}
                                  min={0}
                                />
                              </Form.Item>
                            )}
                          </Form.Item>
                        )}

                        {supportedFileFormats &&
                          selectedRestrictions.includes("supportedInputFormats") && (
                            <Form.Item
                              label="Supported File Input Formats (Upload)"
                              name="supportedInputFormats"
                              tooltip="Leave empty to allow all formats"
                            >
                              <Checkbox.Group>
                                <Space direction="vertical">
                                  {supportedFileFormats.inputFormats.map((fmt) => (
                                    <Checkbox key={fmt.value} value={fmt.value}>
                                      {fmt.label}
                                    </Checkbox>
                                  ))}
                                </Space>
                              </Checkbox.Group>
                            </Form.Item>
                          )}

                        {supportedFileFormats &&
                          selectedRestrictions.includes("supportedOutputFormats") && (
                            <Form.Item
                              label="Supported File Output Formats (Export/Download)"
                              name="supportedOutputFormats"
                              tooltip="Leave empty to allow all formats"
                            >
                              <Checkbox.Group>
                                <Space direction="vertical">
                                  {supportedFileFormats.outputFormats.map((fmt) => (
                                    <Checkbox key={fmt.value} value={fmt.value}>
                                      {fmt.label}
                                    </Checkbox>
                                  ))}
                                </Space>
                              </Checkbox.Group>
                            </Form.Item>
                          )}
                      </Space>
                    </Form.Item>
                  </>
                )}

                {priceType === "addon" && (
                  <>
                    <Form.Item
                      label="Add-On Type"
                      name="addOnType"
                      rules={[
                        {
                          required: true,
                          message: "Please select add-on type",
                        },
                      ]}
                    >
                      <Radio.Group onChange={handleAddOnTypeChange}>
                        <Radio value="FeatureAddOn">Features</Radio>
                        <Radio value="EnhancedLimitsAddOn">Limits</Radio>
                        <Radio value="FeaturesAndEnhancedLimitsAddOn">
                          Features and Limits
                        </Radio>
                      </Radio.Group>
                    </Form.Item>

                    {(addOnType === "FeatureAddOn" ||
                      addOnType === "FeaturesAndEnhancedLimitsAddOn") && (
                      <Form.Item
                        label="Select Features"
                        name="selectedFeatures"
                        rules={[
                          {
                            required: true,
                            message: "Please select at least one feature",
                          },
                        ]}
                      >
                        <Select
                          mode="multiple"
                          allowClear
                          placeholder="Please select the features"
                          showSearch
                          onSearch={handleSearchFeatureGroups}
                          filterOption={false}
                          notFoundContent={
                            duplicatedFeatureGroups.length === 0
                              ? "No features found"
                              : null
                          }
                        >
                          {duplicatedFeatureGroups?.map((feature: any) => (
                            <Option key={feature.id} value={feature.id}>
                              {feature.displayName}
                            </Option>
                          ))}
                        </Select>
                      </Form.Item>
                    )}

                    {(addOnType === "EnhancedLimitsAddOn" ||
                      addOnType === "FeaturesAndEnhancedLimitsAddOn") && (
                      <>
                        <Form.Item
                          label="Select Limits"
                          name="selectedLimits"
                          rules={[
                            {
                              required: true,
                              message: "Please select at least one limit",
                            },
                          ]}
                        >
                          <Select
                            mode="multiple"
                            allowClear
                            placeholder="Please select the limits"
                            onChange={handleLimitsChange}
                          >
                            <Option value="extraUsers">Extra Users</Option>
                            <Option value="extraAiTokens">
                              Extra AI Tokens
                            </Option>
                            <Option value="extraScheduledJobs">
                              Extra Scheduled Jobs
                            </Option>
                            <Option value="extraRowsProcessed">
                              Extra Rows Processed
                            </Option>
                            <Option value="extraPagesAllowed">
                              Extra Pages Allowed
                            </Option>
                            <Option value="extraValidationRulesAllowed">
                              Extra Validation Rules Allowed
                            </Option>
                            <Option value="extraApiConnectorsAllowed">
                              Extra Api Connectors Allowed
                            </Option>
                            <Option value="extraAutomationWorkflowsAllowed">
                              Extra Automation Workflows Allowed
                            </Option>
                            <Option value="extraEmailIntegrationsAllowed">
                              Extra Email Integrations Allowed
                            </Option>
                          </Select>
                        </Form.Item>

                        {selectedLimits?.length > 0 && (
                          <Form.Item label="Please Define the Limits">
                            {selectedLimits.map((limit) => (
                              <Form.Item
                                key={limit}
                                name={limit}
                                label={limit
                                  .replace(/([A-Z])/g, " $1")
                                  .replace(/^./, (str) => str.toUpperCase())}
                                rules={[
                                  {
                                    required: true,
                                    message: `Please enter ${limit}`,
                                  },
                                ]}
                                style={{ marginBottom: 4 }}
                              >
                                <InputNumber
                                  placeholder={`Enter ${limit
                                    .replace(/([A-Z])/g, " $1")
                                    .toLowerCase()}`}
                                  style={{ width: "100%", marginBottom: 2 }}
                                  min={0}
                                />
                              </Form.Item>
                            ))}
                          </Form.Item>
                        )}
                      </>
                    )}

                    <Form.Item
                      label="Has Quantity"
                      name="hasQuantity"
                      valuePropName="checked"
                    >
                      <Checkbox>Has Quantity</Checkbox>
                    </Form.Item>

                    {hasQuantity === true && (
                      <Form.Item
                        label="Quantity"
                        name="quantity"
                        rules={[
                          { required: true, message: "Please enter quantity" },
                        ]}
                      >
                        <InputNumber
                          placeholder="Enter quantity"
                          style={{ width: "100%" }}
                          min={1}
                        />
                      </Form.Item>
                    )}
                  </>
                )}
              </>
            )}

            {priceType === "subscription" && currentStep === 1 && (
              <>
                <Divider />
                <PlanEditor currentPlan={mockSubscriptionTier} />
              </>
            )}

            {(priceType !== "subscription" || currentStep === 0) && (
              <>
                <Form.Item
                  label="Billing Interval"
                  name="billingInterval"
                  rules={[
                    {
                      required: true,
                      message: "Please select billing interval",
                    },
                  ]}
                >
                  <Select
                    placeholder="Please select billing interval"
                    disabled={isEditMode}
                    value={billingInterval}
                    onChange={(value) => {
                      form.setFieldsValue({ billingInterval: value });
                    }}
                  >
                    <Option value="monthly">Monthly</Option>
                    <Option value="yearly">Yearly</Option>
                    <Option value="one-time">One-Time</Option>
                  </Select>

                  {isEditMode && (
                    <div
                      style={{
                        fontSize: "12px",
                        color: "#999",
                        marginTop: "4px",
                      }}
                    >
                      Billing interval cannot be changed after creation
                    </div>
                  )}
                </Form.Item>

                <Form.Item
                  label="Price"
                  name="price"
                  rules={[{ required: true, message: "Please enter price" }]}
                >
                  <InputNumber
                    placeholder="Enter price"
                    style={{ width: "100%" }}
                    min={0}
                    step={0.01}
                    prefix="$"
                  />
                </Form.Item>

                <Form.Item
                  label="Currency"
                  name="currency"
                  initialValue="USD"
                  rules={[
                    { required: true, message: "Please select currency" },
                  ]}
                >
                  <Select>
                    <Option value="USD">USD</Option>
                    <Option value="EUR">EUR</Option>
                    <Option value="GBP">GBP</Option>
                  </Select>
                </Form.Item>

                <Form.Item
                  label="Description"
                  name="description"
                  rules={[
                    { required: true, message: "Please enter description" },
                  ]}
                >
                  <TextArea rows={4} placeholder="Enter description" />
                </Form.Item>
              </>
            )}

            <Form.Item>
              <Space>
                {priceType === "subscription" && currentStep === 1 && (
                  <Button onClick={handlePrevious}>Previous</Button>
                )}
                <Button onClick={handleCancel}>Cancel</Button>
                <Button type="primary" htmlType="submit">
                  {priceType === "subscription" && currentStep === 0
                    ? "Next"
                    : isEditMode
                    ? "Update Price"
                    : "Create Price"}
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Card>
      </div>

      <FeatureSelectionModal
        isOpen={isFeatureModalOpen}
        onClose={() => setIsFeatureModalOpen(false)}
        onConfirm={handleFeatureConfirm}
        initialSelectedFeatures={selectedFeatureGroups}
        title="Select Feature Groups"
      />
    </div>
  );
};

export default AddPriceToProductPage;
